// Synth instrument characters: each one is a cute creature built from its classic instrument's
// physical parts, so the pad's sound engine is recognisable at a glance.
//   woodwump   Mono   — walnut-cheeked panel, knob eyes/nose, rows of knobs, keyboard teeth,
//                        patch-cable tail, keyboard feet
//   acidmite   Acid   — brushed-silver box bug, step buttons on its back (a lit step chases),
//                        knob eyes, 13-key keyboard mouth, blinking LED
//   choruspede Poly   — caterpillar whose segments carry keyboards, fader antennae, doubled
//                        "chorus" shimmer wings
//   operatoad  FM     — flat brown slab toad, LCD-screen face (eyes on the screen), green
//                        membrane-button warts, slim keyboard lips
//   drawbear   Organ  — wooden cabinet bear, drawbar mohawk (brown/white/black tips), spinning
//                        rotary-speaker hat
// Holding a note shows the instrument working: keys light up, LEDs glow, the rotor speeds up.
// Idle motion is slow (≤ ~1 Hz); fast motion only while hitting/holding.
import { Container, Graphics } from "pixi.js";
import { INK, mix } from "../palette";
import { Rng } from "../lib/rng";
import type { CreatureGenome } from "./genome";
import { blob, blot, chord, disc, inkLine, noodle, oval } from "./draw";
import {
  bodyOval,
  drawBackSpikes,
  drawBlush,
  drawKeys,
  drawKnob,
  drawMohawk,
  drawTufts,
  eyeLayout,
  limb,
  newAnatomyParts,
  pal,
  sides,
  type Anatomy,
  type Colors,
  type Geo,
} from "./parts";

const PANEL = 0x2e2a3a;
const SILVER_LINE = 0xffffff;
const LIT = 0xf0e442;
const LED = 0xd55e00;

const holdAmt = (p: { state: string }) => (p.state === "hold" ? 1 : 0);

// ---------------------------------------------------------------------------------------------

export function woodwump(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const legH = H * 0.1 * gn.limbLength,
    ry = H * 0.33 * gn.heightScale,
    rx = ry * gn.bodyAspect,
    cy = -legH - ry;
  const cheekW = H * 0.15 * (0.85 + 0.3 * gn.span);
  const e = eyeLayout(gn, H, rx * 0.95, cy - ry * 0.05, 1.05);
  const geo: Geo = {
    cy,
    rx,
    ry,
    centerY: cy,
    halfW: rx + cheekW,
    headTopY: cy - ry - H * 0.06,
    headW: rx * 1.6,
    topY: cy - ry - H * 0.12,
    ...e,
    mouthY: cy + ry * 0.55,
    mouthW: rx * 1.25 * (0.75 + gn.mouthWidth * 0.5),
    neckY: cy + ry * 0.92,
    neckHalfW: rx + cheekW * 0.5,
    floats: false,
    eyeStyle: "knob",
    mouthStyle: "keys",
    mouthKeys: 8,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();
  // Patch-cable tail with a jack plug.
  const tail = limb(back, rx * 0.7, cy + ry * 0.6, "tail", 1, 0, 0);
  limbs.push(tail.l);
  drawers.push((c) => {
    const g = tail.g;
    g.clear();
    const L = H * 0.32 * gn.tailLength;
    const pts = [0, 0, L * 0.6, H * 0.04, L, -L * 0.3, L * 0.75, -L * 0.6, L * 1.05, -L * 0.75];
    noodle(g, pts, H * 0.035, pal(c, 0, 0xd55e00), ow * 0.8, true);
    const jx = L * 1.05,
      jy = -L * 0.75;
    g.roundRect(jx - H * 0.03, jy - H * 0.09, H * 0.06, H * 0.09, H * 0.015)
      .fill({ color: 0xc9ccd3 })
      .stroke({ width: ow * 0.8, color: INK });
    noodle(g, [jx, jy - H * 0.09, jx, jy - H * 0.15], H * 0.015, 0xc9ccd3, ow * 0.6);
  });
  // Keyboard feet.
  for (const s of sides) {
    const foot = limb(back, s * rx * 0.5, -legH * 1.3, "leg", s, 0, 0);
    limbs.push(foot.l);
    drawers.push(() => {
      foot.g.clear();
      drawKeys(foot.g, -H * 0.13, legH * 1.3 - H * 0.08, H * 0.26, H * 0.08, 4, ow * 0.8);
    });
  }
  const knobRow = 4 + (gn.palette.length > 1 ? 1 : 0);
  const drawBody = (c: Colors) => {
    const g = body;
    g.clear();
    drawMohawk(g, gn, 0, cy - ry, H, c, ow);
    drawTufts(g, gn, 0, cy - ry, rx, H, c.base, ow);
    drawBackSpikes(g, gn, 0, cy - ry * 0.3, rx, ry * 0.8, H, c, ow, -0.8, -0.2);
    // Panel.
    g.roundRect(-rx, cy - ry, rx * 2, ry * 2, H * 0.08)
      .fill({ color: mix(PANEL, c.base, 0.35) })
      .stroke({ width: ow, color: INK });
    // Walnut cheeks with grain.
    for (const s of sides) {
      const x0 = s > 0 ? rx - cheekW * 0.25 : -rx - cheekW * 0.75;
      g.roundRect(x0, cy - ry - H * 0.08, cheekW, ry * 2 + H * 0.1, cheekW * 0.45)
        .fill({ color: c.base })
        .stroke({ width: ow, color: INK });
      for (let k = 0; k < 3; k++)
        inkLine(
          g,
          [
            x0 + cheekW * (0.3 + k * 0.2),
            cy - ry,
            x0 + cheekW * (0.25 + k * 0.22),
            cy,
            x0 + cheekW * (0.32 + k * 0.2),
            cy + ry * 0.9,
          ],
          ow * 0.4,
          true,
          mix(c.base, INK, 0.45),
        );
    }
    // Row of knobs along the top (the filter / oscillator controls).
    for (let i = 0; i < knobRow; i++) {
      const x = -rx * 0.75 + (i / (knobRow - 1)) * rx * 1.5;
      drawKnob(g, x, cy - ry * 0.68, H * 0.045, i % 2 ? c.accent : pal(c, i >> 1, c.accent), ow * 0.7, -1.2 + i * 0.6);
    }
    // Knob nose.
    drawKnob(g, 0, e.eyeY + e.eyeR * 0.85, H * 0.045, c.accent, ow * 0.7, 0.3);
    drawBlush(g, gn, e.eyeY + e.eyeR * 0.2, e.eyeDx, e.eyeR);
  };
  return { geo, back, body, front, limbs, drawers, drawBody };
}

// ---------------------------------------------------------------------------------------------

export function acidmite(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const legH = H * 0.13 * gn.limbLength,
    ry = H * 0.27 * gn.heightScale,
    rx = ry * gn.bodyAspect * 1.05,
    cy = -legH - ry;
  const e = eyeLayout(gn, H, rx * 0.9, cy - ry * 0.1, 1.0);
  const geo: Geo = {
    cy,
    rx,
    ry,
    centerY: cy,
    halfW: rx + H * 0.12,
    headTopY: cy - ry - H * 0.05,
    headW: rx * 1.4,
    topY: cy - ry - H * 0.25,
    ...e,
    mouthY: cy + ry * 0.55,
    mouthW: rx * 1.3 * (0.8 + gn.mouthWidth * 0.35),
    neckY: cy + ry * 0.95,
    neckHalfW: rx,
    floats: false,
    eyeStyle: "knob",
    mouthStyle: "keys",
    mouthKeys: 8,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();
  for (const s of sides) {
    const legs = limb(back, s * rx * 0.55, cy + ry * 0.8, "leg", s, 0, 0);
    limbs.push(legs.l);
    drawers.push((c) => {
      legs.g.clear();
      for (let i = 0; i < gn.legPairs; i++) {
        const sx = -s * i * rx * 0.25,
          kx = s * (rx * 0.35 + i * H * 0.02) * (0.85 + 0.2 * gn.span),
          fy = -(cy + ry * 0.8);
        noodle(legs.g, [sx, 0, kx, fy * 0.35, kx + s * H * 0.05, fy], H * 0.035, mix(c.base, INK, 0.45), ow * 0.75);
      }
    });
  }
  // Step buttons along the back; one lit step chases along (slow at idle, fast on hold).
  const steps = 8;
  const stepW = (rx * 1.7) / steps;
  const stepY = cy - ry - H * 0.035;
  const lit = new Graphics();
  // LED on a little stalk.
  const ledX = rx * 0.7,
    ledY = cy - ry - H * 0.16;
  const ledGlow = new Graphics();
  ledGlow.blendMode = "add";
  ledGlow.position.set(ledX, ledY);
  const drawBody = (c: Colors) => {
    const g = body;
    g.clear();
    drawMohawk(g, gn, -rx * 0.3, cy - ry, H, c, ow);
    drawTufts(g, gn, -rx * 0.2, cy - ry, rx * 0.8, H, c.base, ow);
    noodle(g, [ledX, cy - ry, ledX, ledY], H * 0.02, mix(c.base, INK, 0.4), ow * 0.6);
    disc(g, ledX, ledY, H * 0.03, mix(LED, INK, 0.35), ow * 0.7);
    for (let i = 0; i < steps; i++) {
      const x = -rx * 0.85 + i * stepW;
      g.roundRect(x + stepW * 0.12, stepY - H * 0.02, stepW * 0.76, H * 0.06, H * 0.012)
        .fill({ color: i % 4 === 0 ? c.accent : 0xe9e9ee })
        .stroke({ width: ow * 0.6, color: INK });
    }
    drawBackSpikes(g, gn, 0, cy, rx, ry, H, c, ow, -0.95, -0.05);
    // Brushed-silver box.
    g.roundRect(-rx, cy - ry, rx * 2, ry * 2, H * 0.06)
      .fill({ color: c.base })
      .stroke({ width: ow, color: INK });
    for (let k = 1; k < 7; k++) {
      const y = cy - ry + (k / 7) * ry * 2;
      g.moveTo(-rx + H * 0.03, y)
        .lineTo(rx - H * 0.03, y)
        .stroke({ width: Math.max(0.6, ow * 0.3), color: k % 2 ? SILVER_LINE : mix(c.base, INK, 0.25), alpha: 0.5 });
    }
    // Six little control knobs (tune, cutoff, reso, env, decay, accent).
    for (let i = 0; i < 6; i++)
      drawKnob(
        g,
        -rx * 0.75 + (i / 5) * rx * 1.5,
        cy - ry * 0.68,
        H * 0.032,
        gn.markings.multi ? pal(c, i) : 0x2b2540,
        ow * 0.6,
        -1 + i * 0.45,
      );
    drawBlush(g, gn, e.eyeY, e.eyeDx, e.eyeR);
  };
  drawers.push((c) => {
    lit.clear();
    lit
      .roundRect(stepW * 0.12, stepY - H * 0.02, stepW * 0.76, H * 0.06, H * 0.012)
      .fill({ color: mix(LED, 0xffffff, 0.15) })
      .stroke({ width: ow * 0.6, color: INK });
    ledGlow.clear();
    ledGlow.circle(0, 0, H * 0.09).fill({ color: LED, alpha: 0.35 });
    ledGlow.circle(0, 0, H * 0.03).fill({ color: mix(LED, 0xffffff, 0.4) });
    void c;
  });
  front.addChild(lit, ledGlow);
  const phase = (gn.seed % 997) / 997;
  let stepPos = phase * steps;
  let lastT = 0;
  const tick: Anatomy["tick"] = (p, t, hitAmt) => {
    const dt = Math.max(0, Math.min(0.1, t - lastT));
    lastT = t;
    const rate = p.state === "sleep" ? 0 : p.state === "hold" ? 10 : hitAmt > 0 ? 6 : 1.2; // steps/s
    stepPos = (stepPos + dt * rate) % steps;
    lit.x = -rx * 0.85 + Math.floor(stepPos) * stepW;
    lit.visible = p.state !== "sleep";
    // LED: slow soft blink at idle (~0.5 Hz), solid on hit / hold.
    const idle = 0.5 + 0.5 * Math.sin((t * 0.5 + phase) * Math.PI * 2);
    ledGlow.alpha = p.state === "sleep" ? 0 : Math.max(idle * 0.8, hitAmt, holdAmt(p));
  };
  return { geo, back, body, front, limbs, drawers, drawBody, tick };
}

// ---------------------------------------------------------------------------------------------

export function choruspede(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const hr = H * 0.24,
    hy = -hr - H * 0.04;
  const segs = 4 + Math.round((gn.sizeScale - 0.8) * 3);
  const segR = H * 0.17;
  const hump = H * 0.26 * gn.heightScale;
  const segStep = segR * 1.4 * (0.85 + 0.15 * gn.bodyAspect);
  const segPos: { x: number; y: number; r: number }[] = [];
  for (let i = 1; i <= segs; i++) {
    const t = i / segs;
    const r = segR * (1 - t * 0.25);
    segPos.push({ x: hr * 0.5 + i * segStep, y: -r - Math.sin(t * Math.PI) * hump, r });
  }
  const e = eyeLayout(gn, H, hr * 1.15, hy - hr * 0.1, 0.9);
  const geo: Geo = {
    cy: hy,
    rx: hr,
    ry: hr,
    centerY: hy,
    halfW: hr + segs * segStep * 0.5,
    headTopY: hy - hr,
    headW: hr * 1.7,
    topY: hy - hr - H * 0.32 * gn.limbLength,
    ...e,
    mouthY: hy + hr * 0.5,
    mouthW: gn.mouthWidth * hr * 1.8,
    neckY: hy + hr * 0.9,
    neckHalfW: hr * 0.8,
    floats: false,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();
  // Chorus wings: two doubled translucent copies (chorus = detuned doubling), shimmer on hold.
  const mid = segPos[Math.floor(segs / 2)];
  const wings = new Container();
  wings.position.set(mid.x, mid.y - mid.r * 0.6);
  back.addChild(wings);
  const wingA = new Graphics(),
    wingB = new Graphics();
  wings.addChild(wingB, wingA);
  drawers.push((c) => {
    for (const [g, dx, col] of [
      [wingA, 0, mix(0xd6f0ff, c.light, 0.2)],
      [wingB, H * 0.05, pal(c, 0, 0xcc79a7)],
    ] as [Graphics, number, number][]) {
      g.clear();
      for (const s of sides) {
        g.ellipse(s * H * 0.13 * gn.span + dx, -H * 0.12, H * 0.14 * gn.span, H * 0.075)
          .fill({ color: col, alpha: 0.7 })
          .stroke({ width: ow * 0.7, color: INK, alpha: 0.9 });
      }
    }
    wingB.alpha = 0.6;
  });
  // Little legs under each segment (static).
  const legs = new Graphics();
  back.addChild(legs);
  drawers.push((c) => {
    legs.clear();
    // Stubby legs only where a segment touches down (inchworm arch in the middle).
    for (const s of segPos)
      if (s.y + s.r > -H * 0.12)
        for (const k of [-0.35, 0.35])
          noodle(
            legs,
            [s.x + k * s.r, s.y + s.r * 0.6, s.x + k * s.r * 1.15, -H * 0.01],
            H * 0.04,
            mix(c.base, INK, 0.3),
            ow * 0.6,
          );
  });
  // Segments with keyboards on their backs.
  const segG = new Graphics();
  back.addChild(segG);
  const litG = new Graphics();
  back.addChild(litG);
  const segKeys = (g: Graphics, s: { x: number; y: number; r: number }, litKey: number) =>
    drawKeys(g, s.x - s.r * 0.78, s.y - s.r * 0.72, s.r * 1.56, s.r * 0.8, 4, ow * 0.7, litKey);
  drawers.push((c) => {
    segG.clear();
    for (let i = segPos.length - 1; i >= 0; i--) {
      const s = segPos[i];
      bodyOval(segG, gn, s.x, s.y, s.r, s.r, c.base, ow);
      segG
        .moveTo(s.x - s.r * 0.9, s.y + s.r * 0.25)
        .quadraticCurveTo(s.x, s.y + s.r * 0.45, s.x + s.r * 0.9, s.y + s.r * 0.25)
        .stroke({ width: ow * 1.1, color: i % 2 && gn.markings.multi ? pal(c, i) : c.accent, cap: "round" });
      segKeys(segG, s, -1);
    }
    // Lit overlay (shown on hold / hit).
    litG.clear();
    for (let i = 0; i < segPos.length; i++) segKeys(litG, segPos[i], (i * 3) % 4);
  });
  litG.visible = false;
  // Fader antennae: slot + sliding cap.
  const caps: Graphics[] = [];
  const faderL = H * 0.26 * gn.limbLength * (0.8 + 0.25 * gn.tailLength);
  for (const s of sides) {
    const a = limb(back, s * hr * 0.4, hy - hr * 0.75, "antenna", 0, s > 0 ? 1 : 0, s * 0.18);
    limbs.push(a.l);
    const cap = new Graphics();
    a.l.node.addChild(cap);
    caps.push(cap);
    drawers.push((c) => {
      a.g.clear();
      a.g
        .roundRect(-H * 0.022, -faderL, H * 0.044, faderL, H * 0.022)
        .fill({ color: PANEL })
        .stroke({ width: ow * 0.7, color: INK });
      cap.clear();
      cap
        .roundRect(-H * 0.05, -H * 0.03, H * 0.1, H * 0.06, H * 0.015)
        .fill({ color: 0xf4efe1 })
        .stroke({ width: ow * 0.7, color: INK });
      cap
        .moveTo(-H * 0.035, 0)
        .lineTo(H * 0.035, 0)
        .stroke({ width: ow * 0.5, color: pal(c, 1, c.accent) });
    });
  }
  const drawBody = (c: Colors) => {
    const g = body;
    g.clear();
    drawMohawk(g, gn, 0, hy - hr, H, c, ow);
    drawBackSpikes(g, gn, 0, hy, hr, hr, H, c, ow, -0.8, -0.2);
    bodyOval(g, gn, 0, hy, hr * 1.05, hr, c.base, ow);
    // Racing stripe across the forehead + light muzzle.
    g.moveTo(-hr * 0.85, hy - hr * 0.42)
      .quadraticCurveTo(0, hy - hr * 0.62, hr * 0.85, hy - hr * 0.42)
      .stroke({ width: ow * 1.4, color: c.accent, cap: "round" });
    blot(g, 0, hy + hr * 0.45, hr * 0.55, hr * 0.32, c.belly);
    drawBlush(g, gn, e.eyeY, e.eyeDx, e.eyeR);
  };
  const phase = ((gn.seed % 997) / 997) * 6.28;
  const tick: Anatomy["tick"] = (p, t, hitAmt) => {
    const hold = holdAmt(p);
    litG.visible = hold > 0 || hitAmt > 0.3;
    if (litG.visible) litG.alpha = hold ? 0.75 + 0.25 * Math.sin(t * 9) : hitAmt;
    // Chorus shimmer: the doubled wing drifts against the other (slow at idle).
    const rate = hold ? 5 : 1.1;
    wingB.x = Math.sin(t * rate + phase) * H * (hold ? 0.03 : 0.012);
    wingA.alpha = 0.85 + 0.15 * Math.sin(t * rate * 0.7 + phase);
    // Faders slide up when playing, rest low when idle.
    for (let i = 0; i < caps.length; i++) {
      const lvl =
        p.state === "sleep"
          ? 0.1
          : 0.35 +
            0.08 * Math.sin(t * 0.8 + phase + i) +
            0.45 * Math.max(hold * (0.7 + 0.3 * Math.sin(t * 6 + i)), hitAmt);
      caps[i].y = -faderL * lvl;
    }
  };
  void front;
  return { geo, back, body, front, limbs, drawers, drawBody, tick };
}

// ---------------------------------------------------------------------------------------------

export function operatoad(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const legH = H * 0.07,
    ry = H * 0.22 * gn.heightScale,
    rx = ry * gn.bodyAspect * 1.25,
    cy = -legH - ry;
  const scrW = rx * 0.95,
    scrH = ry * 0.85,
    scrY = cy - ry * 0.42;
  const lcd = 0xb9d36c;
  const e0 = eyeLayout(gn, H, scrW, scrY, 0.9);
  const eyeR = Math.min(e0.eyeR, scrH * 0.32);
  const e = { eyeR, eyeDx: Math.min(Math.max(e0.eyeDx, eyeR * 1.4), scrW * 0.38), eyeY: scrY };
  const geo: Geo = {
    cy,
    rx,
    ry,
    centerY: cy,
    halfW: rx + H * 0.12,
    headTopY: cy - ry - H * 0.06,
    headW: rx * 1.2,
    topY: cy - ry - H * 0.12,
    ...e,
    mouthY: cy + ry * 0.55,
    mouthW: rx * 1.1 * (0.8 + gn.mouthWidth * 0.3),
    neckY: cy + ry * 0.92,
    neckHalfW: rx * 0.9,
    floats: false,
    eyeStyle: "screen",
    screenColor: lcd,
    mouthStyle: "keys",
    mouthKeys: 10,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();
  for (const s of sides) {
    const leg = limb(back, s * rx * 0.7, cy + ry * 0.55, "leg", s, 0, 0);
    limbs.push(leg.l);
    const fy = -(cy + ry * 0.55);
    drawers.push((c) => {
      leg.g.clear();
      oval(leg.g, s * H * 0.1, fy * 0.3, H * 0.14 * gn.limbLength, H * 0.1, mix(c.base, INK, 0.1), ow);
      blob(
        leg.g,
        [
          s * H * 0.08,
          fy - H * 0.02,
          s * H * 0.28 * gn.span,
          fy - H * 0.04,
          s * H * 0.3 * gn.span,
          fy,
          s * H * 0.05,
          fy,
        ],
        c.accent,
        ow,
        true,
      );
    });
  }
  // Membrane-button warts (green), seeded positions on the top and the slab.
  const rng = new Rng(gn.seed ^ 0xd7);
  const warts: { x: number; y: number }[] = [];
  const nw = 5 + Math.round(gn.traits.colorfulness * 4);
  for (let i = 0; i < nw; i++) {
    const side = i % 2 ? 1 : -1;
    warts.push({ x: side * rng.range(scrW * 0.55, rx * 0.9), y: cy + rng.range(-ry * 0.85, ry * 0.15) });
  }
  const backlight = new Graphics();
  backlight.blendMode = "add";
  front.addChild(backlight);
  drawers.push(() => {
    backlight.clear();
    backlight.roundRect(-scrW / 2, scrY - scrH / 2, scrW, scrH, H * 0.02).fill({ color: 0xe8ff9a, alpha: 0.45 });
  });
  const drawBody = (c: Colors) => {
    const g = body;
    g.clear();
    drawMohawk(g, gn, 0, cy - ry, H, c, ow);
    drawTufts(g, gn, 0, cy - ry, rx, H, c.base, ow);
    drawBackSpikes(g, gn, 0, cy, rx, ry, H, c, ow, -0.85, -0.15);
    // Eye bumps on top, crowned with membrane buttons.
    for (const s of sides) {
      disc(g, s * rx * 0.58, cy - ry * 0.95, H * 0.12, c.base, ow);
      g.roundRect(s * rx * 0.58 - H * 0.045, cy - ry * 0.95 - H * 0.085, H * 0.09, H * 0.055, H * 0.012)
        .fill({ color: c.accent })
        .stroke({ width: ow * 0.6, color: INK });
    }
    // Slab.
    g.roundRect(-rx, cy - ry, rx * 2, ry * 2, H * 0.07)
      .fill({ color: c.base })
      .stroke({ width: ow, color: INK });
    g.roundRect(-rx + ow, cy - ry + ow, rx * 2 - ow * 2, ry * 0.18, H * 0.03).fill({
      color: mix(c.base, 0xffffff, 0.15),
    });
    // LCD screen.
    g.roundRect(-scrW / 2 - H * 0.02, scrY - scrH / 2 - H * 0.02, scrW + H * 0.04, scrH + H * 0.04, H * 0.03)
      .fill({ color: PANEL })
      .stroke({ width: ow * 0.8, color: INK });
    g.roundRect(-scrW / 2, scrY - scrH / 2, scrW, scrH, H * 0.02).fill({ color: lcd });
    // Membrane buttons.
    warts.forEach((w, i) => {
      g.roundRect(w.x - H * 0.03, w.y - H * 0.02, H * 0.06, H * 0.04, H * 0.01)
        .fill({ color: gn.markings.multi && i % 2 ? pal(c, i >> 1) : c.accent })
        .stroke({ width: ow * 0.55, color: INK });
    });
    blot(g, 0, cy + ry * 0.55, rx * 0.7, ry * 0.32, c.belly, 0.5);
    drawBlush(g, gn, cy + ry * 0.3, rx * 0.55, H * 0.06);
  };
  const tick: Anatomy["tick"] = (p, t, hitAmt) => {
    const hold = holdAmt(p);
    backlight.alpha =
      p.state === "sleep"
        ? 0
        : Math.max(0.15 + 0.08 * Math.sin(t * 0.9), hitAmt * 0.9, hold * (0.8 + 0.2 * Math.sin(t * 7)));
  };
  return { geo, back, body, front, limbs, drawers, drawBody, tick };
}

// ---------------------------------------------------------------------------------------------

const DRAWBAR_TIPS = [0x7a3f1d, 0x7a3f1d, 0xfaf7ee, 0xfaf7ee, INK, 0xfaf7ee, INK, INK, 0xfaf7ee];

export function drawbear(gn: CreatureGenome, H: number, ow: number): Anatomy {
  const legH = H * 0.07 * gn.limbLength,
    ry = H * 0.38 * gn.heightScale,
    rx = ry * gn.bodyAspect * 0.92,
    cy = -legH - ry;
  const faceY = cy - ry * 0.3;
  const e = eyeLayout(gn, H, rx * 0.9, faceY - ry * 0.12, 0.9);
  const snoutY = e.eyeY + e.eyeR * 1.7;
  const geo: Geo = {
    cy,
    rx,
    ry,
    centerY: cy,
    halfW: rx + H * 0.08,
    headTopY: cy - ry,
    headW: rx * 1.2,
    topY: cy - ry - H * 0.32,
    ...e,
    mouthY: snoutY + H * 0.1,
    mouthW: gn.mouthWidth * rx * 1.1,
    neckY: snoutY + H * 0.14,
    neckHalfW: rx * 1.01,
    floats: false,
  };
  const { back, front, body, limbs, drawers } = newAnatomyParts();
  for (const s of sides) {
    const leg = limb(back, s * rx * 0.55, -legH * 1.3, "leg", s, 0, 0);
    limbs.push(leg.l);
    drawers.push((c) => {
      leg.g.clear();
      oval(leg.g, 0, legH * 1.0, H * 0.1, H * 0.06, mix(c.base, INK, 0.2), ow);
    });
    const arm = limb(front, s * rx * 0.92, cy + ry * 0.05, "arm", s, 0, 0.3);
    limbs.push(arm.l);
    drawers.push((c) => {
      arm.g.clear();
      oval(arm.g, 0, H * 0.09 * gn.span, H * 0.06, H * 0.11 * gn.span, c.base, ow);
    });
  }
  // Drawbars: registration (pull-out lengths) seeded; tips in Hammond brown/white/black.
  const rng = new Rng(gn.seed ^ 0xd8a);
  const nBars = 5 + Math.round(gn.traits.colorfulness * 3);
  const bars = Array.from({ length: nBars }, () => rng.int(2, 8) / 8);
  const barsW = rx * 1.05;
  // Rotary speaker hat (tilted, right side): cabinet + spinning horn.
  const hat = new Container();
  hat.position.set(rx * 0.68, cy - ry + H * 0.03);
  hat.rotation = 0.22;
  const hatBox = new Graphics();
  const horn = new Graphics();
  horn.position.set(0, -H * 0.22);
  hat.addChild(hatBox, horn);
  front.addChild(hat);
  drawers.push((c) => {
    hatBox.clear();
    hatBox
      .roundRect(-H * 0.13, -H * 0.17, H * 0.26, H * 0.18, H * 0.035)
      .fill({ color: mix(c.base, INK, 0.15) })
      .stroke({ width: ow, color: INK });
    for (let k = 0; k < 3; k++)
      hatBox
        .moveTo(-H * 0.08, -H * 0.125 + k * H * 0.04)
        .lineTo(H * 0.08, -H * 0.125 + k * H * 0.04)
        .stroke({ width: ow * 0.6, color: INK });
    noodle(hatBox, [0, -H * 0.17, 0, -H * 0.2], H * 0.025, 0xc9ccd3, ow * 0.5);
    horn.clear();
    // Double horn: two flared bells on a shaft.
    for (const s of sides)
      blob(
        horn,
        [
          0,
          -H * 0.015,
          s * H * 0.12,
          -H * 0.035,
          s * H * 0.16,
          -H * 0.055,
          s * H * 0.16,
          H * 0.055,
          s * H * 0.12,
          H * 0.035,
          0,
          H * 0.015,
        ],
        s > 0 ? 0xc9ccd3 : mix(0xc9ccd3, INK, 0.25),
        ow * 0.8,
        false,
      );
    disc(horn, 0, 0, H * 0.025, pal(c, 0, c.accent), ow * 0.6);
  });
  // Organ manuals on the belly; lit when holding.
  const kw = rx * 0.95,
    kx = -kw / 2,
    ky = cy + ry * 0.6,
    kh = H * 0.045;
  const litKeys = new Graphics();
  front.addChild(litKeys);
  drawers.push(() => {
    litKeys.clear();
    drawKeys(litKeys, kx, ky, kw, kh, 7, ow * 0.6, 2, LIT);
    drawKeys(litKeys, kx + kw * 0.04, ky + kh * 1.15, kw * 0.92, kh, 7, ow * 0.6, 4, LIT);
  });
  litKeys.visible = false;
  const drawBody = (c: Colors) => {
    const g = body;
    g.clear();
    // Ears.
    for (const s of sides) {
      disc(g, s * rx * 0.72, cy - ry * 0.95, H * 0.09 * gn.span, c.base, ow);
      blot(g, s * rx * 0.72, cy - ry * 0.93, H * 0.045 * gn.span, H * 0.045 * gn.span, c.belly);
    }
    // Drawbar mohawk.
    for (let i = 0; i < nBars; i++) {
      const x = -barsW * 0.62 + ((i + 0.5) / nBars) * barsW * 0.85;
      const L = H * (0.05 + 0.15 * bars[i]) * (0.8 + 0.4 * gn.limbLength);
      const top = cy - ry - L;
      g.rect(x - H * 0.014, top, H * 0.028, L + H * 0.04)
        .fill({ color: 0xc9ccd3 })
        .stroke({ width: ow * 0.5, color: INK });
      g.roundRect(x - H * 0.03, top - H * 0.07, H * 0.06, H * 0.08, H * 0.015)
        .fill({ color: gn.markings.multi && i % 3 === 2 ? pal(c, i) : DRAWBAR_TIPS[i % DRAWBAR_TIPS.length] })
        .stroke({ width: ow * 0.7, color: INK });
    }
    drawMohawk(g, gn, -rx * 0.3, cy - ry, H, c, ow);
    drawTufts(g, gn, -rx * 0.3, cy - ry, rx * 0.5, H, c.base, ow);
    drawBackSpikes(g, gn, 0, cy, rx, ry, H, c, ow, -0.95, -0.7);
    // Cabinet.
    g.roundRect(-rx, cy - ry, rx * 2, ry * 2, H * 0.1)
      .fill({ color: c.base })
      .stroke({ width: ow, color: INK });
    for (const s of sides)
      inkLine(
        g,
        [s * rx * 0.8, cy - ry * 0.6, s * rx * 0.75, cy, s * rx * 0.82, cy + ry * 0.7],
        ow * 0.45,
        true,
        mix(c.base, INK, 0.4),
      );
    // Face panel + snout + nose.
    g.roundRect(-rx * 0.68, faceY - ry * 0.5, rx * 1.36, ry * 0.85, H * 0.08).fill({
      color: mix(c.belly, c.base, 0.2),
    });
    oval(g, 0, snoutY, H * 0.11, H * 0.075, c.belly, ow * 0.7);
    blot(g, 0, snoutY - H * 0.035, H * 0.035, H * 0.025, INK);
    // Organ manuals.
    drawKeys(g, kx, ky, kw, kh, 7, ow * 0.6);
    drawKeys(g, kx + kw * 0.04, ky + kh * 1.15, kw * 0.92, kh, 7, ow * 0.6);
    drawBlush(g, gn, e.eyeY, e.eyeDx, e.eyeR);
  };
  let angle = ((gn.seed % 997) / 997) * 6.28;
  let lastT = 0;
  const tick: Anatomy["tick"] = (p, t, hitAmt) => {
    const dt = Math.max(0, Math.min(0.1, t - lastT));
    lastT = t;
    const hold = holdAmt(p);
    // Rotor: slow "chorale" at idle (~0.3 rev/s), fast "tremolo" while holding.
    const revPerSec = p.state === "sleep" ? 0 : hold ? 2.2 : 0.3 + hitAmt * 1.5;
    angle += dt * revPerSec * Math.PI * 2;
    horn.scale.x = Math.cos(angle);
    litKeys.visible = hold > 0 || hitAmt > 0.3;
  };
  void chord;
  return { geo, back, body, front, limbs, drawers, drawBody, tick };
}
